import { useMemo } from 'react';

interface DemoRestrictions {
  pointPurchase: boolean;
  shippingAddress: boolean;
  prizeClaim: boolean;
}

interface DemoRestrictionHook {
  isDemo: boolean;
  restrictions: DemoRestrictions;
  getRestrictionMessage: (feature: keyof typeof MESSAGES) => string | null;
  hasRestriction: (feature: keyof DemoRestrictions) => boolean;
}

const MESSAGES = {
  pointPurchase: 'デモ版ではポイント購入はご利用いただけません',
  shippingAddress: 'デモ版では配送先登録はご利用いただけません',
  prizeClaim: 'デモ版では賞品受取はご利用いただけません',
  upgrade: '全機能を使うには店舗開設が必要です',
} as const;

export const useDemoRestrictions = (siteSubdomain?: string): DemoRestrictionHook => {
  // サブドメインからデモ判定（未指定の場合はURLから取得）
  const subdomain = useMemo(() => {
    if (siteSubdomain) return siteSubdomain;

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      // demo.joybase.jp または demo.joybase.localhost
      if (hostname.startsWith('demo.')) return 'demo';
    }

    return null;
  }, [siteSubdomain]);

  const isDemo = subdomain === 'demo';

  const restrictions: DemoRestrictions = useMemo(() => ({
    pointPurchase: isDemo,
    shippingAddress: isDemo,
    prizeClaim: isDemo,
  }), [isDemo]);

  const getRestrictionMessage = (feature: keyof typeof MESSAGES): string | null => {
    if (!isDemo) return null;
    return MESSAGES[feature];
  };

  const hasRestriction = (feature: keyof DemoRestrictions): boolean => {
    return restrictions[feature] === true;
  };

  return {
    isDemo,
    restrictions,
    getRestrictionMessage,
    hasRestriction,
  };
};
